import axios from 'axios'
import { getAccessToken } from './auth'

// ---------------------------------------------------------------------------
// Axios instance — all requests go through the Vite dev proxy at /api/intelligence
// which forwards to the connected intelligence service and injects CORS headers.
// Authentication uses the same bearer token as the Databricks APIs.
// ---------------------------------------------------------------------------
const client = axios.create({
  baseURL: '/api/intelligence/',
  headers: { 'Content-Type': 'application/json' },
})

client.interceptors.request.use(async (config) => {
  try {
    config.headers.Authorization = `Bearer ${await getAccessToken()}`
  } catch {
    return config
  }
  return config
})

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 400) {
      const body = await error.response?.data
      console.error('Intelligence API 400 error:', body)
    }
    return Promise.reject(error)
  }
)

// ---------------------------------------------------------------------------
// Types — contract for the Prism Intelligence service
// ---------------------------------------------------------------------------

export interface IntelligenceAsset {
  id: string
  name: string
  description?: string
  type?: string
  owner?: string
  classification?: string
  quality_score?: number
  qualityScore?: number
}

export interface DocumentationSection {
  title: string
  summary: string
  items: string[]
}

export interface IntelligenceProduct {
  id: string
  name: string
  slug?: string
  description?: string
  domain?: string
  owner?: string
  product_type?: string
  availability?: string
  tags?: string[]
  metrics_label?: string
  dimensions_label?: string
  time_grain_label?: string
  access_method?: string
  refresh_cadence?: string
  coverage?: string
  use_cases?: string[]
  highlights?: string[]
  documentation_url?: string
  documentation?: DocumentationSection[]
}

export interface IntelligenceModel {
  id: string
  name: string
  description?: string
  version?: string
  stage?: string
  owner?: string
  framework?: string
  governance?: {
    riskLevel?: string
  }
}

export interface LineageNode {
  id: string
  name?: string
  type?: string
  kind?: string
  system?: string
  metadata?: Record<string, string>
}

export interface LineageEdge {
  source: string
  target: string
  kind?: string
  type?: string
  transformation_type?: string
  description?: string
}

export interface LineageGraph {
  asset_id?: string
  nodes?: LineageNode[]
  edges?: LineageEdge[]
}

export interface IntelligencePolicy {
  id: string
  name: string
  description?: string
  status?: string
  category?: string
  appliesTo?: string[]
}

export interface IntelligenceAgent {
  id: string
  name: string
  description?: string
  kind?: string
  endpoint?: string
  status?: string
  version?: string
  owner_id?: string
  risk_level?: string
}

export interface IntelligenceRegistryResource {
  id: string
  name: string
  kind?: string
  status?: string
  description?: string
  owner_id?: string
  version?: string
}

export interface IntelligenceInsight {
  id: string
  title: string
  summary?: string
  body_markdown?: string
  status?: string
  author_id?: string
  author_name?: string
  tags?: string[]
  content_type?: 'markdown' | 'pdf'
  pdf_file_id?: string
  pdf_filename?: string
  created_at?: string
  updated_at?: string
  published_at?: string | null
}

export interface IntelligenceInsightInput {
  title?: string
  summary?: string
  body_markdown?: string
  tags?: string[]
  status?: 'draft' | 'published'
  content_type?: 'markdown' | 'pdf'
}

export interface ChatSource {
  id: string
  name: string
  type: string
  description: string
  relevance_score: number
  metadata: Record<string, string>
}

export interface ChatResponse {
  response?: string
  message?: string
  conversation_id?: string
  sources?: ChatSource[]
}

export interface CheckoutResponse {
  request_id: string
  status: string
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const intelligenceApi = {
  listAssets: () =>
    client.get<IntelligenceAsset[]>('/assets').then(({ data }) => data),
  searchAssets: (query: string) =>
    client
      .get<IntelligenceAsset[]>('/assets/search', { params: { q: query } })
      .then(({ data }) => data),
  listProducts: () =>
    client
      .get<IntelligenceProduct[]>('/catalog/products')
      .then(({ data }) => data),
  getProduct: (slug: string) =>
    client
      .get<IntelligenceProduct>(`/catalog/products/${slug}`)
      .then(({ data }) => data),
  checkout: (productIds: string[], businessPurpose: string) =>
    client
      .post<CheckoutResponse>('/catalog/checkout', {
        product_ids: productIds,
        business_purpose: businessPurpose,
      })
      .then(({ data }) => data),
  listModels: () =>
    client.get<IntelligenceModel[]>('/models').then(({ data }) => data),
  getLineage: (assetId: string) =>
    client.get<LineageGraph>(`/lineage/${assetId}`).then(({ data }) => data),
  listPolicies: () =>
    client
      .get<IntelligencePolicy[]>('/governance/policies')
      .then(({ data }) => data),
  listAgents: () =>
    client.get<IntelligenceAgent[]>('/agent/agents').then(({ data }) => data),
  chat: (message: string, conversationId?: string) =>
    client
      .post<ChatResponse>('/agent/chat', {
        message,
        conversation_id: conversationId,
      })
      .then(({ data }) => data),
  listRegistryResources: () =>
    client
      .get<IntelligenceRegistryResource[]>('/registry/resources')
      .then(({ data }) => data),
  listInsights: () =>
    client
      .get<IntelligenceInsight[]>('/insights')
      .then(({ data }) => data),
  createInsight: (title: string, summary: string, bodyMarkdown: string) =>
    client
      .post<IntelligenceInsight>('/insights', {
        title,
        summary,
        body_markdown: bodyMarkdown,
        status: 'draft',
      })
      .then(({ data }) => data),
  getInsight: (id: string) =>
    client.get<IntelligenceInsight>(`/insights/${id}`).then(({ data }) => data),
  updateInsight: (id: string, input: Partial<IntelligenceInsightInput>) =>
    client
      .put<IntelligenceInsight>(`/insights/${id}`, input)
      .then(({ data }) => data),
  removeInsight: (id: string) => client.delete(`/insights/${id}`),
  getPolicy: (id: string) =>
    client
      .get<IntelligencePolicy>(`/governance/policies/${id}`)
      .then(({ data }) => data),
}
